// frontend/src/components/ModalOrdenCompra.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  Typography,
  LinearProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import api from '../services/api';

function ModalOrdenCompra({ idfactura, onClose }) {
  const gridApiRef = useGridApiRef();

  const [proveedores, setProveedores] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]); // solo para UI
  const [vistaPrevia, setVistaPrevia] = useState({}); // Orden: idproveedor -> base64
  const [vistaPacking, setVistaPacking] = useState(''); // Packing / Invoice: base64 único
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [accionCargando, setAccionCargando] = useState(false);

  // 'orden' | 'packing' | 'invoice'
  const [tipoReporte, setTipoReporte] = useState('orden');

  // ✅ NUEVO: para invoice -> adjuntar packing
  const [adjuntarPackingEnInvoice, setAdjuntarPackingEnInvoice] = useState(false);
  const [invoiceCorregido, setInvoiceCorregido] = useState(false);

  // Cargar proveedores de la factura
  useEffect(() => {
    if (!idfactura) return;

    const cargarProveedores = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/facturas/${idfactura}/proveedores`);
        setProveedores(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('❌ Error al cargar proveedores:', err);
        setProveedores([]);
      } finally {
        setLoading(false);
      }
    };

    cargarProveedores();
  }, [idfactura]);

  // Filas para el grid
  const rows = useMemo(
    () =>
      (proveedores || []).map((p) => ({
        id: p.idproveedor,
        nombre: p.nombre,
        correo: p.correo
      })),
    [proveedores]
  );

  const columns = useMemo(
    () => [
      { field: 'nombre', headerName: 'Proveedor', flex: 1, minWidth: 220 },
      {
        field: 'correo',
        headerName: 'Correo',
        flex: 1,
        minWidth: 230,
        renderCell: (params) => params.value || '— sin correo —'
      }
    ],
    []
  );

  const handleRowSelectionChange = useCallback((newModel) => {
    if (Array.isArray(newModel)) {
      setSeleccionados(newModel.map((id) => Number(id)));
    } else {
      setSeleccionados([]);
    }
  }, []);

  const obtenerIdsSeleccionados = useCallback(() => {
    if (tipoReporte === 'invoice') return [];
    let ids = Array.isArray(seleccionados) ? seleccionados.map(Number) : [];

    if (
      !ids.length &&
      gridApiRef.current &&
      typeof gridApiRef.current.getSelectedRows === 'function'
    ) {
      try {
        const rowsMap = gridApiRef.current.getSelectedRows();
        if (rowsMap && typeof rowsMap.keys === 'function') {
          ids = Array.from(rowsMap.keys()).map(Number);
        }
      } catch (e) {
        console.warn('Grid API warning:', e);
      }
    }
    return ids;
  }, [seleccionados, gridApiRef, tipoReporte]);

  const haySeleccion = obtenerIdsSeleccionados().length > 0;
  const requiereSeleccion = tipoReporte === 'orden';
  const hayVistaOrden = Object.keys(vistaPrevia || {}).length > 0;
  const hayVistaPacking = !!vistaPacking;
  const hayVista = hayVistaOrden || hayVistaPacking;

  // ----------------------------------------------------------------------
  // Helpers PDF: descargar / imprimir (base64)
  // ----------------------------------------------------------------------

  const base64ToBlob = useCallback((base64, mime = 'application/pdf') => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  }, []);

  const guardarConSavePicker = useCallback(async (blob, filename) => {
    if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'PDF',
            accept: { 'application/pdf': ['.pdf'] }
          }
        ]
      });

      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    }
    return false;
  }, []);

  const descargarPdfBase64 = useCallback(
    async (base64, filename) => {
      if (!base64) return;

      const safeName = filename || `Documento_${idfactura}.pdf`;
      try {
        const blob = base64ToBlob(base64, 'application/pdf');

        const guardado = await guardarConSavePicker(blob, safeName);
        if (guardado) return;

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = safeName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error('❌ Error al descargar/guardar PDF:', e);
        alert('No se pudo guardar el archivo. Intente nuevamente.');
      }
    },
    [idfactura, base64ToBlob, guardarConSavePicker]
  );

  const imprimirPdfSeguroBase64 = useCallback((base64) => {
    if (!base64) return;

    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error('Error al imprimir frame:', e);
            alert('No se pudo iniciar la impresión automática. Intente descargar el archivo.');
          }
        }, 500);
      };
    } catch (e) {
      console.error('Error preparando impresión:', e);
      alert('Error preparando el documento para imprimir.');
    }
  }, []);

  // ----------------------------------------------------------------------
  // Ver PDF
  // ----------------------------------------------------------------------
  const handleVer = useCallback(async () => {
    if (!idfactura) return;

    let proveedoresIds = [];
    if (tipoReporte === 'invoice') {
      proveedoresIds = [];
    } else {
      proveedoresIds = obtenerIdsSeleccionados();
    }

    if (tipoReporte === 'orden' && proveedoresIds.length === 0) {
      return;
    }

    setAccionCargando(true);
    setNombreArchivo('');

    try {
      const body = { proveedores: proveedoresIds, formato: tipoReporte };

      if (tipoReporte === 'orden') {
        const res = await api.post(`/api/facturas/${idfactura}/orden/ver`, body);
        setVistaPrevia(res.data || {});
        setVistaPacking('');
      } else {
        const res = await api.post(
          `/api/facturas/${idfactura}/packing/ver?formato=${encodeURIComponent(tipoReporte)}`,
          body
        );
        setVistaPacking(res.data?.base64 || '');
        if (res.data?.filename) {
          setNombreArchivo(res.data.filename);
        }
        setVistaPrevia({});
      }
    } catch (err) {
      console.error('❌ Error en ver():', err);
    } finally {
      setAccionCargando(false);
    }
  }, [idfactura, tipoReporte, obtenerIdsSeleccionados]);

  useEffect(() => {
    const ids = obtenerIdsSeleccionados();

    if (tipoReporte === 'invoice' || tipoReporte === 'packing') {
      handleVer();
    } else if (tipoReporte === 'orden' && ids.length > 0) {
      handleVer();
    }
  }, [tipoReporte, obtenerIdsSeleccionados]); // eslint-disable-line react-hooks/exhaustive-deps

  const descargarActual = async () => {
    if (!vistaPacking) return;
    await descargarPdfBase64(vistaPacking, nombreArchivo || `Documento_${idfactura}.pdf`);
  };

  const imprimirPDFSeguro = () => {
    if (!vistaPacking) return;
    imprimirPdfSeguroBase64(vistaPacking);
  };

  // Enviar PDF por correo
  const handleEnviar = useCallback(async () => {
    if (!idfactura) return;

    let proveedoresIds = [];
    if (tipoReporte === 'invoice') {
      proveedoresIds = [];
    } else {
      proveedoresIds = obtenerIdsSeleccionados();
    }

    if (tipoReporte === 'orden' && proveedoresIds.length === 0) {
      alert('Selecciona al menos un proveedor para enviar la Orden de compra.');
      return;
    }

    setAccionCargando(true);
    try {
      // ✅ body base
      const body = { proveedores: proveedoresIds, formato: tipoReporte };

      if (tipoReporte === 'orden') {
        await api.post(`/api/facturas/${idfactura}/orden/enviar`, body);
        alert('📤 Órdenes enviadas a los proveedores con éxito');
      } else {
        // ✅ NUEVO: si es invoice, mandamos el flag adjuntarPacking
        const bodyEnviar = {
          ...body,
          ...(tipoReporte === 'invoice'
            ? { adjuntarPacking: !!adjuntarPackingEnInvoice, invoiceCorregido: !!invoiceCorregido }
            : {})
        };

        const respuesta = await api.post(
          `/api/facturas/${idfactura}/packing/enviar?formato=${encodeURIComponent(tipoReporte)}`,
          bodyEnviar
        );

        if (tipoReporte === 'invoice') {
          const entrega = respuesta?.data?.entrega;
          const aceptados = Array.isArray(entrega?.acceptedPrimary)
            ? entrega.acceptedPrimary.join(', ')
            : '';
          const messageId = entrega?.messageId || '';
          const evidencia = [
            aceptados ? `Aceptado por el servidor para: ${aceptados}` : '',
            messageId ? `ID del mensaje: ${messageId}` : ''
          ]
            .filter(Boolean)
            .join('\n');

          alert(
            `${
              adjuntarPackingEnInvoice
                ? 'Invoice + Packing aceptados por el servidor de correo.'
                : 'Invoice aceptado por el servidor de correo.'
            }${evidencia ? `\n\n${evidencia}` : ''}`
          );
        } else {
          alert('📤 Packing List enviado con éxito');
        }
      }
      onClose?.();
    } catch (err) {
      console.error('❌ Error en enviar():', err);
      const detalle = err?.response?.data?.error || err?.message || 'Error al enviar el correo';
      alert(`No se pudo enviar el correo:\n${detalle}`);
    } finally {
      setAccionCargando(false);
    }
  }, [
    idfactura,
    tipoReporte,
    obtenerIdsSeleccionados,
    onClose,
    adjuntarPackingEnInvoice,
    invoiceCorregido
  ]);

  const deshabilitarAcciones = accionCargando || (requiereSeleccion && !haySeleccion);

  const handleDescargarExcel = async () => {
    try {
      if (!idfactura) return;

      // ✅ OJO: aquí va /api para que NO caiga en el frontend (index.html)
      const resp = await api.get(`/api/facturas/${idfactura}/invoice/excel`, {
        responseType: 'blob'
      });

      const cd = resp.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/i);
      const filename = match?.[1] || `INVOICE_${idfactura}.xlsx`;

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Descargar Excel error:', e);
      alert('No se pudo descargar el Excel');
    }
  };

  return (
    <Dialog
      open
      onClose={() => onClose?.()}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          spacing={1.5}
        >
          <Box>
            <Typography variant="h6">📦 Reportes de Factura</Typography>
            <Typography variant="body2" color="text.secondary">
              Factura #{idfactura}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Tipo de reporte:</Typography>
            <select
              value={tipoReporte}
              onChange={(e) => {
                const v = e.target.value;
                setTipoReporte(v);
                setVistaPrevia({});
                setVistaPacking('');
                setNombreArchivo('');

                // ✅ si sales de invoice, apaga el check
                // ✅ si sales de invoice, apaga los checks
                if (v !== 'invoice') {
                  setAdjuntarPackingEnInvoice(false);
                  setInvoiceCorregido(false);
                }

                if (v === 'invoice') {
                  setSeleccionados([]);
                  if (gridApiRef.current && gridApiRef.current.setRowSelectionModel) {
                    try {
                      gridApiRef.current.setRowSelectionModel([]);
                    } catch (ignore) {}
                  }
                }
              }}
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: 14
              }}
            >
              <option value="orden">Orden de Compra (por proveedor)</option>
              <option value="packing">Packing (consolidado)</option>
              <option value="invoice">Comercial Invoice (cliente)</option>
            </select>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          py: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        {(loading || accionCargando) && <LinearProgress sx={{ mb: 1 }} />}

        {tipoReporte === 'invoice' && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Comercial Invoice se enviará al correo del <strong>cliente</strong> registrado en la
              factura.
            </Typography>

            {/* ✅ NUEVO: opción adjuntar packing */}
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  checked={adjuntarPackingEnInvoice}
                  onChange={(e) => setAdjuntarPackingEnInvoice(e.target.checked)}
                />
              }
              label="Adjuntar también el Packing List en el mismo correo"
            />
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  checked={invoiceCorregido}
                  onChange={(e) => setInvoiceCorregido(e.target.checked)}
                />
              }
              label="Invoice corregido (agregar “CORRECTION” al asunto)"
            />
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md:
                tipoReporte === 'invoice'
                  ? '1fr'
                  : hayVista
                    ? 'minmax(0, 1.1fr) minmax(0, 1fr)'
                    : '1fr'
            },
            columnGap: 2,
            rowGap: 2,
            height: '100%',
            minHeight: 0
          }}
        >
          {/* Columna izquierda: grid de proveedores */}
          {tipoReporte !== 'invoice' && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Typography variant="subtitle2">Proveedores ({rows.length})</Typography>

                <Typography variant="caption" color="text.secondary">
                  Seleccionados: {seleccionados.length}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0 }}>
                {rows.length === 0 ? (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      color: 'text.secondary',
                      fontSize: 14
                    }}
                  >
                    No hay proveedores en esta factura.
                  </Box>
                ) : (
                  <DataGridPremium
                    apiRef={gridApiRef}
                    rows={rows}
                    columns={columns}
                    getRowId={(r) => r.id}
                    checkboxSelection
                    disableColumnMenu
                    density="compact"
                    loading={loading}
                    onRowSelectionModelChange={handleRowSelectionChange}
                    sx={{
                      height: '100%',
                      '& .MuiDataGrid-main': { height: '100%' },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto',
                        overflowX: 'auto',
                        scrollbarGutter: 'stable both-edges',
                        overscrollBehavior: 'contain'
                      }
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  fontSize: 12,
                  color: 'text.secondary'
                }}
              >
                {tipoReporte === 'packing' ? (
                  <>
                    Si no seleccionas ninguno, se incluirán <strong>todos</strong>.
                  </>
                ) : (
                  <>Se generará una Orden por cada proveedor seleccionado.</>
                )}
              </Box>
            </Box>
          )}

          {/* Columna derecha: Vista Previa PDF */}
          {hayVista && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}
            >
              <Box
                sx={{
                  p: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Vista previa PDF</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {nombreArchivo ||
                      (tipoReporte === 'orden'
                        ? 'Órdenes generadas por proveedor.'
                        : 'Documento consolidado.')}
                  </Typography>
                </Box>

                {(tipoReporte === 'invoice' || tipoReporte === 'packing') && vistaPacking && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <span
                      onClick={imprimirPDFSeguro}
                      title="Imprimir"
                      style={{ cursor: 'pointer', fontSize: '1.5rem', userSelect: 'none' }}
                    >
                      🖨️
                    </span>
                    <span
                      onClick={descargarActual}
                      title={`Guardar ${nombreArchivo || 'PDF'}`}
                      style={{ cursor: 'pointer', fontSize: '1.5rem', userSelect: 'none' }}
                    >
                      💾
                    </span>
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflowY: 'auto'
                }}
              >
                {tipoReporte === 'orden' &&
                  Object.entries(vistaPrevia).map(([id, base64]) => {
                    const filename = `OrdenCompra_${idfactura}_Prov_${id}.pdf`;
                    return (
                      <Box
                        key={id}
                        sx={{
                          mb: 1,
                          height: { xs: 420, md: 'calc(80vh - 220px)' },
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 0.5 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'text.secondary' }}
                          >
                            Proveedor #{id}
                          </Typography>

                          <Stack direction="row" spacing={2} alignItems="center">
                            <span
                              onClick={() => imprimirPdfSeguroBase64(base64)}
                              title="Imprimir orden"
                              style={{ cursor: 'pointer', fontSize: '1.35rem', userSelect: 'none' }}
                            >
                              🖨️
                            </span>
                            <span
                              onClick={() => descargarPdfBase64(base64, filename)}
                              title={`Guardar ${filename}`}
                              style={{ cursor: 'pointer', fontSize: '1.35rem', userSelect: 'none' }}
                            >
                              💾
                            </span>
                          </Stack>
                        </Stack>

                        <Box
                          component="iframe"
                          src={`data:application/pdf;base64,${base64}#toolbar=0`}
                          title={`Orden proveedor ${id}`}
                          sx={{
                            width: '100%',
                            flex: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    );
                  })}

                {(tipoReporte === 'packing' || tipoReporte === 'invoice') && vistaPacking && (
                  <Box
                    sx={{
                      height: { xs: 480, md: 'calc(80vh - 220px)' },
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box
                      component="iframe"
                      src={`data:application/pdf;base64,${vistaPacking}#toolbar=0&navpanes=0&scrollbar=0`}
                      title={`${tipoReporte === 'packing' ? 'Packing' : 'Invoice'} ${idfactura}`}
                      sx={{
                        width: '100%',
                        flex: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="outlined" onClick={handleVer} disabled={deshabilitarAcciones}>
            {tipoReporte === 'orden'
              ? '👁 Ver Orden'
              : tipoReporte === 'packing'
                ? '👁 Ver Packing'
                : '👁 Ver Invoice'}
          </Button>

          <Button variant="contained" onClick={handleEnviar} disabled={deshabilitarAcciones}>
            {tipoReporte === 'orden'
              ? '📤 Enviar Orden'
              : tipoReporte === 'packing'
                ? '📤 Enviar Packing'
                : adjuntarPackingEnInvoice
                  ? '📤 Enviar Invoice + Packing'
                  : '📤 Enviar Invoice'}
          </Button>
          <Button
            variant="contained"
            onClick={handleDescargarExcel}
            disabled={tipoReporte !== 'invoice'}
          >
            DESCARGAR EXCEL
          </Button>
          <Button variant="text" onClick={() => onClose?.()} disabled={accionCargando}>
            Cerrar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default ModalOrdenCompra;
